import { useId, useState, type FormEvent } from "react";
import { authService } from "../../features";
import { Button, Notice } from "../ui";
import { AuthCard } from "./AuthCard";
import { PasswordInput } from "./PasswordInput";

/**
 * Where a password reset link lands: the link signs the person in, and this
 * asks for the new password before they carry on to their site.
 */
export const NewPasswordForm = ({ onDone }: { onDone: () => void }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const passwordId = useId();
  const confirmPasswordId = useId();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Those passwords don’t match. Please try again.");
      return;
    }
    setSaving(true);
    const failure = await authService.updatePassword(password);
    setSaving(false);
    if (failure) setError(failure);
    else setSaved(true);
  };

  if (saved)
    return (
      <AuthCard title="Password updated" intro="Use your new password next time you log in.">
        <Notice tone="success">Your password has been changed.</Notice>
        <Button variant="primary" size="lg" fullWidth className="mt-5" onClick={onDone}>
          Continue to your site
        </Button>
      </AuthCard>
    );

  return (
    <AuthCard title="Choose a new password" intro="Pick something at least 6 characters long.">
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label className="text-sm font-medium" htmlFor={passwordId}>
            New password
          </label>
          <PasswordInput
            autoComplete="new-password"
            autoFocus
            className="mt-2"
            id={passwordId}
            minLength={6}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="At least 6 characters"
            required
            value={password}
          />
        </div>
        <div>
          <label className="text-sm font-medium" htmlFor={confirmPasswordId}>
            Confirm new password
          </label>
          <PasswordInput
            autoComplete="new-password"
            className="mt-2"
            id={confirmPasswordId}
            minLength={6}
            onChange={(event) => setConfirmPassword(event.target.value)}
            placeholder="Type it again"
            required
            value={confirmPassword}
          />
        </div>
        {error && <Notice>{error}</Notice>}
        <Button variant="primary" size="lg" fullWidth disabled={saving} type="submit">
          {saving ? "Saving…" : "Save password"}
        </Button>
      </form>
    </AuthCard>
  );
};
